import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Divider,
  Chip,
} from "@mui/material";
import { AlertOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { GetPendingInvoiceSummary } from "../api/hooks";
import useAuth from "hooks/useAuth";

const formatAmount = (value: number) =>
  value == null ? "0" : new Intl.NumberFormat("en-IN").format(value);

export default function PendingInvoicesAlertModal() {
  const { modules } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigateTo = useNavigate();

  const { data: summaryQuery } = GetPendingInvoiceSummary();
  const pendingList = summaryQuery?.apiData || [];

  useEffect(() => {
    const isShown = sessionStorage.getItem("pending_invoices_alert_shown");
    const hasPermission = Boolean(modules?.invoice?.permissions?.View);


    if (
      isShown !== "true" &&
      !dismissed &&
      hasPermission &&
      pendingList.length > 0
    ) {
      const timer = setTimeout(() => {
        setOpen(true);
        sessionStorage.setItem("pending_invoices_alert_shown", "true");
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [pendingList, dismissed, modules]);

  const handleReviewNow = () => {
    setDismissed(true);
    setOpen(false);
    sessionStorage.setItem("pending_invoices_alert_shown", "true");
    navigateTo("/admin/budget/invoice");
  };

  const handleRemindLater = () => {
    setDismissed(true);
    setOpen(false);
    sessionStorage.setItem("pending_invoices_alert_shown", "true");
  };

  if (!open || pendingList.length === 0) return null;

  const totalLockedAmount = pendingList.reduce(
    (sum, inv) => sum + (inv.totalAmount || 0),
    0,
  );

  return (
    <Dialog open={open} onClose={handleRemindLater} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1.5 }}
      >
        <AlertOutlined style={{ color: "#faad14", fontSize: 24 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Pending Invoices Action Required
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You have <strong>{pendingList.length}</strong> pending invoice(s)
            holding <strong>₹ {formatAmount(totalLockedAmount)}</strong> in
            budget funds.
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Box
          sx={{
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell sx={{ fontWeight: 600 }}>Invoice No</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Fund</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Amount (₹)
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Pending For
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingList.map((row) => (
                <TableRow key={row.invoiceID} hover>
                  <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                    {row.invoiceNo}
                  </TableCell>
                  <TableCell>{row.supplierName}</TableCell>
                  <TableCell>{row.fundName}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    ₹ {formatAmount(row.totalAmount)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={row.statusName}
                      color={row.status === 1 ? "warning" : "info"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="caption"
                      color={
                        row.daysPending > 3 ? "error.main" : "text.secondary"
                      }
                      fontWeight={600}
                    >
                      {row.daysPending} day(s)
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions
        sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1.5 }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={handleRemindLater}
        >
          I'll Do It Later
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowRightOutlined />}
          onClick={handleReviewNow}
        >
          Review Invoices Now
        </Button>
      </DialogActions>
    </Dialog>
  );
}
